import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  Grid,
  Paper,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import axios from "../../api";
import { useParams } from "react-router-dom";
import { useSnackbar } from "components/AlertMessages/SnackbarContext";
import { useCustomerId } from "context/CustomerIdContext";
import SoftBox from "components/SoftBox";
import { AxiosError } from "axios";
import { ConfirmationNumberOutlined } from "@mui/icons-material";
import moment from "moment";

interface TicketItem {
  ticket_id: number;
  title: string;
  status_name: string;
  status_id: number;
  category_name: string;
  created_at: string;
  ticket_service_id?: string;
  asset_name?: string;
}

interface TicketStatus {
  status_id: number;
  status_name: string;
}

interface ViewCustomerTicketsProps {
  /** When provided, clicking a ticket closes this overlay and parent opens ticket detail overlay */
  onOpenTicketDetail?: (ticketId: number) => void;
  /** When provided, "Add Ticket" is shown; clicking closes this overlay and parent opens Add Ticket overlay with this customer */
  onOpenAddTicket?: (customerId: number) => void;
}

const ViewCustomerTickets = ({ onOpenTicketDetail, onOpenAddTicket }: ViewCustomerTicketsProps = {}) => {
  const { fetchError } = useSnackbar();
  const contextId = useCustomerId();
  const { id: paramId } = useParams();
  const id = contextId ?? paramId;

  const [tickets, setTickets] = useState<TicketItem[] | null>(null);
  const [statuses, setStatuses] = useState<TicketStatus[]>([]);
  const [statusFilter, setStatusFilter] = useState<number | "all">("all");
  const [timeFilter, setTimeFilter] = useState<"all" | "last30">("all");

  const fetchTickets = useCallback(async () => {
    if (!id) return;
    try {
      const response = await axios.get(`/tickets/customer/${id}`);
      setTickets(response?.data?.list ?? []);
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        fetchError(error.response.data.error);
      } else {
        setTickets([]);
      }
    }
  }, [id, fetchError]);

  const fetchStatuses = useCallback(async () => {
    try {
      const response = await axios.get("/ticket-statuses");
      setStatuses(Array.isArray(response?.data) ? response.data : []);
    } catch {
      setStatuses([]);
    }
  }, []);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  useEffect(() => {
    fetchStatuses();
  }, [fetchStatuses]);

  const filteredTickets = useMemo(() => {
    if (!tickets) return [];
    let list = tickets;
    if (statusFilter !== "all") {
      list = list.filter((t) => t.status_id === statusFilter);
    }
    if (timeFilter === "last30") {
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - 30);
      list = list.filter((t) => new Date(t.created_at) >= cutoff);
    }
    return list;
  }, [tickets, statusFilter, timeFilter]);

  const customerIdNum = id != null ? Number(id) : null;

  return (
    <Card sx={{ p: 3, maxWidth: "100%", background: "#fff" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1, mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>
          Manage Tickets
        </Typography>
        {onOpenAddTicket != null && customerIdNum != null && !isNaN(customerIdNum) && (
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => onOpenAddTicket(customerIdNum)}
          >
            Add Ticket
          </Button>
        )}
      </Box>

      {/* Quick filters: status + "Created in last 30 days" */}
      <Box sx={{ mb: 2, display: "flex", flexWrap: "wrap", gap: 0.75 }}>
        <Chip
          label="All"
          size="small"
          color={statusFilter === "all" ? "primary" : "default"}
          variant={statusFilter === "all" ? "filled" : "outlined"}
          onClick={() => setStatusFilter("all")}
          sx={{ fontSize: "0.75rem" }}
        />
        {statuses.map((s) => (
          <Chip
            key={s.status_id}
            label={s.status_name}
            size="small"
            color={statusFilter === s.status_id ? "primary" : "default"}
            variant={statusFilter === s.status_id ? "filled" : "outlined"}
            onClick={() => setStatusFilter(s.status_id)}
            sx={{ fontSize: "0.75rem" }}
          />
        ))}
        <Box sx={{ flexBasis: "100%", height: 4 }} />
        <Chip
          label="Created in last 30 days"
          size="small"
          color={timeFilter === "last30" ? "secondary" : "default"}
          variant={timeFilter === "last30" ? "filled" : "outlined"}
          onClick={() => setTimeFilter(timeFilter === "last30" ? "all" : "last30")}
          sx={{ fontSize: "0.75rem" }}
        />
      </Box>

      <SoftBox mt={2}>
        <Grid container justifyContent="center">
          <Grid item xs={12} md={12}>
            {filteredTickets.length > 0 ? (
              filteredTickets.map((t) => (
                <Paper
                  key={t.ticket_id}
                  elevation={0}
                  sx={{
                    p: 2.5,
                    mb: 2,
                    borderRadius: "14px",
                    border: "1px solid rgba(224,224,224,0.45)",
                    backgroundColor: "#FAFAFA",
                    cursor: onOpenTicketDetail ? "pointer" : "default",
                    "&:hover": onOpenTicketDetail
                      ? { backgroundColor: "#f0f0f0" }
                      : undefined,
                  }}
                  onClick={() => onOpenTicketDetail?.(t.ticket_id)}
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={1}
                  >
                    <Box display="flex" alignItems="center" gap={1.2}>
                      <Box
                        sx={{
                          width: 34,
                          height: 34,
                          borderRadius: "50%",
                          backgroundColor: "#E3F2FD",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#1976d2",
                        }}
                      >
                        <ConfirmationNumberOutlined sx={{ fontSize: 20 }} />
                      </Box>
                      <Box>
                        <Typography
                          fontSize={17}
                          fontWeight={700}
                          color="#222"
                          sx={{ lineHeight: "20px" }}
                        >
                          {t.title || `#${t.ticket_service_id || t.ticket_id}`}
                        </Typography>
                        <Typography
                          fontSize={13}
                          fontWeight={500}
                          mt={0.5}
                          color="#777"
                        >
                          {t.category_name || "—"} • {t.asset_name || "—"}
                        </Typography>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        px: 1.4,
                        py: 0.5,
                        borderRadius: "20px",
                        fontSize: "12px",
                        fontWeight: 600,
                        textTransform: "capitalize",
                        backgroundColor: "#E8F5E9",
                        color: "#2E7D32",
                      }}
                    >
                      {t.status_name}
                    </Box>
                  </Box>
                  <Box sx={{ fontSize: 13, color: "#666" }}>
                    Created {moment(t.created_at).format("DD MMM YYYY, HH:mm")}
                  </Box>
                </Paper>
              ))
            ) : (
              <Typography
                fontSize={15}
                fontWeight={600}
                color="#777"
                textAlign="center"
                mt={4}
              >
                No tickets found
              </Typography>
            )}
          </Grid>
        </Grid>
      </SoftBox>
    </Card>
  );
};

export default ViewCustomerTickets;
